// Slim shell for the catalog board's product editor. All draft state lives in the
// productEditor slice (open/close, fields, icon, options, pricing, placement), so
// this component just composes the field sections and wires Save/Close/Delete.
// Save persists the product, its column placement, and pricing via one thunk; a
// brand-new option-bearing product stays open so its options can be added.
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
  FormControlLabel, Switch, Box, CircularProgress,
} from '@mui/material';
import { toggleCatalogItemActive } from '../../../store/admin-catalog-slice.js';
import {
  discardEditor, setDraftField, saveProductDraft,
} from '../../../store/product-editor-slice.js';
import IconPicker from '../IconPicker.jsx';
import ConfirmDialog from '../ConfirmDialog.jsx';
import ProductDetailsFields from './ProductDetailsFields.jsx';
import LearnMoreSection from './LearnMoreSection.jsx';
import ProductOptionsSection from './ProductOptionsSection.jsx';
import ProductIncludesSection from './ProductIncludesSection.jsx';

const ProductEditorDialog = () => {
  const dispatch = useDispatch();
  const { saving, saveError } = useSelector((state) => state.adminCatalog);
  const {
    open, productId, isNew, hasOptions, draft,
  } = useSelector((state) => state.productEditor);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const close = () => dispatch(discardEditor());

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await dispatch(saveProductDraft());
    if (result.ok && !result.keepOpen) close();
  };

  const handleDeactivate = async () => {
    await dispatch(toggleCatalogItemActive({
      resource: 'products', id: productId, isActive: false,
    }));
    setConfirmOpen(false);
    close();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle>{productId ? 'Edit product' : 'New product'}</DialogTitle>
        <DialogContent sx={{
          display: 'flex', flexDirection: 'column', gap: 2, pt: 2,
        }}
        >
          {saveError && <Alert severity="error">{saveError}</Alert>}
          {productId && isNew && hasOptions && (
            <Alert severity="success">Product saved — add its options below.</Alert>
          )}

          <ProductDetailsFields />

          <IconPicker
            value={draft.icon_key || ''}
            onChange={(key) => dispatch(setDraftField({ field: 'icon_key', value: key }))}
          />

          <FormControlLabel
            control={(
              <Switch
                checked={draft.is_active}
                onChange={(e) => dispatch(setDraftField({ field: 'is_active', value: e.target.checked }))}
              />
            )}
            label="Active"
          />

          <LearnMoreSection />

          <ProductOptionsSection />

          <ProductIncludesSection />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {productId && draft.is_active && (
              <Button color="error" onClick={() => setConfirmOpen(true)}>Delete</Button>
            )}
          </Box>
          <Box>
            <Button onClick={close}>Close</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ ml: 1 }}>
              {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Deactivate product?"
        message={`“${draft.name}” will be hidden from the public form. Existing inquiries keep their record. You can reactivate it later.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onCancel={() => setConfirmOpen(false)}
      />
    </Dialog>
  );
};

export default ProductEditorDialog;
